import {Voyage} from './_models'

export type ReportAssignmentScope = {
  companyGroupAdminId: number
  companyAdminId: number | null
  vesselType: string
  vesselId: number
}

const toPositiveId = (value: unknown): number | null => {
  const numericValue = Number(value)
  return Number.isFinite(numericValue) && numericValue > 0 ? Math.trunc(numericValue) : null
}

const normalizeVesselType = (value: unknown): string | null => {
  const vesselType = String(value ?? '').trim()
  return vesselType ? vesselType : null
}

const firstPositiveId = (...values: unknown[]): number | null => {
  for (const value of values) {
    const id = toPositiveId(value)
    if (id) return id
  }
  return null
}

const firstVesselType = (...values: unknown[]): string | null => {
  for (const value of values) {
    const vesselType = normalizeVesselType(value)
    if (vesselType) return vesselType
  }
  return null
}

const isCrewUser = (currentUser: any): boolean => {
  const behavior = String(
    currentUser?.tenant?.effectiveBehaviorType ??
      currentUser?.effectiveBehaviorType ??
      currentUser?.role?.effectiveBehaviorType ??
      currentUser?.role?.roleType ??
      currentUser?.role?.roleName ??
      ''
  )
    .trim()
    .toUpperCase()

  return behavior === 'CREW'
}

const buildScope = (candidate: {
  companyGroupAdminId?: unknown
  companyAdminId?: unknown
  vesselType?: unknown
  vesselId?: unknown
}): ReportAssignmentScope | null => {
  const companyGroupAdminId = toPositiveId(candidate.companyGroupAdminId)
  const companyAdminId = toPositiveId(candidate.companyAdminId)
  const vesselId = toPositiveId(candidate.vesselId)
  const vesselType = normalizeVesselType(candidate.vesselType)

  if (!companyGroupAdminId || !vesselId || !vesselType) {
    return null
  }

  return {
    companyGroupAdminId,
    companyAdminId,
    vesselId,
    vesselType,
  }
}

const buildCrewScope = (currentUser: any, selectedVoyage: Voyage | null): ReportAssignmentScope | null => {
  const vesselId = firstPositiveId(
    (selectedVoyage as any)?.vesselId,
    selectedVoyage?.vessel?.id,
    currentUser?.vessel?.id,
    currentUser?.tenant?.vesselId,
    currentUser?.tenant?.vessel?.id,
    currentUser?.vesselId,
    currentUser?.crew?.vesselId
  )

  const companyGroupAdminId = firstPositiveId(
    selectedVoyage?.companyGroupAdminId,
    selectedVoyage?.vessel?.companyGroupAdminId,
    currentUser?.companyGroupAdminId,
    currentUser?.tenant?.companyGroupId,
    currentUser?.tenant?.companyGroupAdminId,
    currentUser?.vessel?.companyGroupAdminId,
    currentUser?.companyGroupAdmin?.id,
    currentUser?.tenant?.companyGroup?.id
  )

  const companyAdminId = firstPositiveId(
    selectedVoyage?.companyAdminId,
    selectedVoyage?.vessel?.companyAdminId,
    currentUser?.companyAdminId,
    currentUser?.tenant?.companyAdminId,
    currentUser?.vessel?.companyAdminId,
    currentUser?.companyAdmin?.id,
    currentUser?.tenant?.company?.id
  )

  const vesselType = firstVesselType(
    (selectedVoyage as any)?.vesselType,
    selectedVoyage?.vessel?.vesselType,
    currentUser?.vessel?.vesselType,
    currentUser?.tenant?.vesselType
  )

  return buildScope({
    companyGroupAdminId,
    companyAdminId,
    vesselId,
    vesselType,
  })
}

export const resolveReportAssignmentScope = ({
  currentUser,
  selectedVoyage,
}: {
  currentUser: any
  selectedVoyage: Voyage | null
}): ReportAssignmentScope | null => {
  if (isCrewUser(currentUser)) {
    return buildCrewScope(currentUser, selectedVoyage)
  }

  const voyageScope = buildScope({
    companyGroupAdminId: selectedVoyage?.companyGroupAdminId,
    companyAdminId: selectedVoyage?.companyAdminId,
    vesselId: (selectedVoyage as any)?.vesselId ?? selectedVoyage?.vessel?.id,
    vesselType: (selectedVoyage as any)?.vesselType ?? selectedVoyage?.vessel?.vesselType,
  })

  if (voyageScope) {
    return voyageScope
  }

  const vessel = currentUser?.vessel
  return buildScope({
    companyGroupAdminId:
      vessel?.companyGroupAdminId ??
      currentUser?.companyGroupAdminId ??
      currentUser?.companyGroupAdmin?.id,
    companyAdminId:
      vessel?.companyAdminId ?? currentUser?.companyAdminId ?? currentUser?.companyAdmin?.id,
    vesselId: vessel?.id,
    vesselType: vessel?.vesselType,
  })
}